/**
 * 魔法陣SVG生成・挿入
 *
 * CLIP STUDIO式の3レイヤー構成:
 * - circlesLayer: 同心円 + 目盛り線
 * - textLayer: 架空文字グリフ（3リング）
 * - geometricLayer: 幾何学模様（パターン別）
 *
 * @see https://tips.clip-studio.com/ja-jp/articles/4553
 */

const INK = "var(--grimoire-ink, #5a3a1a)";

// ── 同心円 + 目盛り ──

function circlesLayer(): string {
  const rings: [number, number][] = [
    [194, 0.3],
    [190, 2.0],
    [186, 0.4],
    [172, 0.7],
    [169, 0.3],
    [155, 0.4],
    [148, 0.4],
    [145, 1.5],
    [142, 0.4],
    [128, 0.3],
    [102, 0.6],
    [99, 0.3],
    [85, 0.25],
    [68, 0.6],
    [65, 0.3],
    [50, 0.3],
    [38, 0.6],
    [35, 0.3],
    [14, 0.5],
  ];

  let s = `<g fill="none" stroke="${INK}">`;
  for (const [r, sw] of rings) {
    s += `<circle cx="200" cy="200" r="${r}" stroke-width="${sw}"/>`;
  }
  s += `<circle cx="200" cy="200" r="5" fill="${INK}" stroke="none"/>`;
  s += `</g>`;

  // 外周目盛り（36本）+ 内周目盛り（12本）
  s += `<g stroke="${INK}" stroke-width="0.4">`;
  for (let i = 0; i < 36; i++) {
    const a = i * 10;
    const long = a % 30 === 0;
    s += `<line x1="200" y1="14" x2="200" y2="${long ? 28 : 21}" transform="rotate(${a},200,200)"/>`;
  }
  for (let i = 0; i < 12; i++) {
    s += `<line x1="200" y1="101" x2="200" y2="110" transform="rotate(${15 + i * 30},200,200)" stroke-width="0.3"/>`;
  }
  s += `</g>`;

  return s;
}

// ── 架空文字（12種のオリジナルSVGパスグリフ） ──

const GLYPHS = [
  "M1,0 V10 M1,3 L5,1",
  "M3,0 L6,5 L3,10 L0,5Z",
  "M0,1 L6,9 M6,1 L0,9",
  "M1,0 V10 M1,3H5 M1,7H5",
  "M0,0 H6 L3,10Z",
  "M0,0 L4,3 L0,7 L4,10",
  "M3,6 V10 M0,3A3,3 0 1,1 6,3A3,3 0 1,1 0,3",
  "M1,0 V10 M5,0 V10",
  "M3,10 V2 L0,5 M3,2 L6,5",
  "M0,4 A3,3 0 0,1 6,4 M3,4 V10",
  "M0,10 V6 H3 V3 H6 V0",
  "M3,10 V5 L0,0 M3,5 L6,0",
];

function glyphRing(
  radius: number,
  count: number,
  scale: number,
  strokeMul: number
): string {
  let s = `<g fill="none" stroke="${INK}" stroke-width="${(0.7 * strokeMul).toFixed(2)}" stroke-linecap="round">`;
  for (let i = 0; i < count; i++) {
    const angle = (i / count) * 360;
    const tx = 200 - 3 * scale;
    const ty = 200 - radius - 10 * scale;
    s += `<g transform="rotate(${angle.toFixed(1)},200,200)">`;
    s += `<path d="${GLYPHS[i % GLYPHS.length]}" transform="translate(${tx},${ty}) scale(${scale})"/>`;
    s += `</g>`;
  }
  s += `</g>`;
  return s;
}

function textLayer(): string {
  return (
    glyphRing(178, 85, 1.0, 1.0) +
    glyphRing(115, 60, 0.7, 0.85) +
    glyphRing(43, 28, 0.45, 0.7)
  );
}

// ── 幾何学模様 ──

/** 正多角形の頂点座標を生成。offsetDeg=0 で頂点が上（12時方向） */
function polyPoints(
  cx: number,
  cy: number,
  r: number,
  n: number,
  offsetDeg = 0
): [number, number][] {
  const result: [number, number][] = [];
  for (let i = 0; i < n; i++) {
    const a = (i / n) * Math.PI * 2 + (offsetDeg * Math.PI) / 180;
    result.push([cx + r * Math.sin(a), cy - r * Math.cos(a)]);
  }
  return result;
}

/** 五芒星 + 六芒星（魔導書タブ デフォルト） */
function geometricLayerPentagram(): string {
  const V: [number, number][] = [
    [200, 55],
    [338, 155],
    [285, 317],
    [115, 317],
    [62, 155],
  ];
  const IV: [number, number][] = [
    [233, 155],
    [253, 217],
    [200, 256],
    [147, 217],
    [167, 155],
  ];
  const HU: [number, number][] = [
    [200, 135],
    [256, 233],
    [144, 233],
  ];
  const HD: [number, number][] = [
    [200, 265],
    [144, 167],
    [256, 167],
  ];
  const lineEnd: [number, number][] = [
    [200, 162],
    [236, 189],
    [222, 231],
    [178, 231],
    [164, 189],
  ];

  const pts = (arr: [number, number][]) =>
    arr.map((p) => p.join(",")).join(" ");

  let s = `<g fill="none" stroke="${INK}">`;

  // 五角形 + 五芒星 + 内五角形
  s += `<polygon points="${pts(V)}" stroke-width="0.5"/>`;
  s += `<polygon points="${pts([0, 2, 4, 1, 3].map((i) => V[i]))}" stroke-width="0.8"/>`;
  s += `<polygon points="${pts(IV)}" stroke-width="0.4"/>`;

  // 六芒星（上向き+下向き三角）
  s += `<polygon points="${pts(HU)}" stroke-width="0.6"/>`;
  s += `<polygon points="${pts(HD)}" stroke-width="0.6"/>`;

  // 放射線（頂点→内部）
  for (let i = 0; i < 5; i++) {
    s += `<line x1="${V[i][0]}" y1="${V[i][1]}" x2="${lineEnd[i][0]}" y2="${lineEnd[i][1]}" stroke-width="0.3"/>`;
  }

  // 頂点装飾（外五角形）
  for (const [x, y] of V) {
    s += `<circle cx="${x}" cy="${y}" r="5" stroke-width="0.5"/>`;
    s += `<circle cx="${x}" cy="${y}" r="2" fill="${INK}" stroke="none"/>`;
  }

  // 内五角形の頂点
  for (const [x, y] of IV) {
    s += `<circle cx="${x}" cy="${y}" r="2.5" fill="${INK}" stroke="none"/>`;
  }

  // 六芒星の頂点
  for (const [x, y] of [...HU, ...HD]) {
    s += `<circle cx="${x}" cy="${y}" r="3" stroke-width="0.4"/>`;
  }

  // 中心ダイヤ
  s += `<polygon points="214,200 200,214 186,200 200,186" stroke-width="0.5"/>`;

  s += `</g>`;
  return s;
}

/** 六芒星（上下三角+中央正方形、サブブックAタブ用） */
function geometricLayerHexagram(): string {
  const triUp = polyPoints(200, 200, 140, 3, 0);
  const triDown = polyPoints(200, 200, 140, 3, 60);
  const triUpIn = polyPoints(200, 200, 88, 3, 0);
  const triDownIn = polyPoints(200, 200, 88, 3, 60);
  const sq = polyPoints(200, 200, 52, 4, 0);
  const outerPts: [number, number][] = [
    triUp[0],
    triDown[0],
    triUp[1],
    triDown[1],
    triUp[2],
    triDown[2],
  ];
  const innerRadial = outerPts.map(([x, y]) => {
    const dx = x - 200,
      dy = y - 200;
    const len = Math.sqrt(dx * dx + dy * dy);
    return [200 + (dx / len) * 90, 200 + (dy / len) * 90] as [number, number];
  });
  const innerPts: [number, number][] = [
    triUpIn[0],
    triDownIn[0],
    triUpIn[1],
    triDownIn[1],
    triUpIn[2],
    triDownIn[2],
  ];

  const f = (arr: [number, number][]) =>
    arr.map((p) => `${p[0].toFixed(1)},${p[1].toFixed(1)}`).join(" ");

  let s = `<g fill="none" stroke="${INK}">`;
  s += `<polygon points="${f(triUp)}" stroke-width="0.8"/>`;
  s += `<polygon points="${f(triDown)}" stroke-width="0.8"/>`;
  s += `<polygon points="${f(triUpIn)}" stroke-width="0.5"/>`;
  s += `<polygon points="${f(triDownIn)}" stroke-width="0.5"/>`;
  s += `<polygon points="${f(sq)}" stroke-width="0.6"/>`;
  for (let i = 0; i < 6; i++) {
    const [x1, y1] = outerPts[i];
    const [x2, y2] = innerRadial[i];
    s += `<line x1="${x1.toFixed(1)}" y1="${y1.toFixed(1)}" x2="${x2.toFixed(1)}" y2="${y2.toFixed(1)}" stroke-width="0.3"/>`;
  }
  for (const [x, y] of outerPts) {
    s += `<circle cx="${x.toFixed(1)}" cy="${y.toFixed(1)}" r="4.5" stroke-width="0.5"/>`;
    s += `<circle cx="${x.toFixed(1)}" cy="${y.toFixed(1)}" r="2" fill="${INK}" stroke="none"/>`;
  }
  for (const [x, y] of innerPts) {
    s += `<circle cx="${x.toFixed(1)}" cy="${y.toFixed(1)}" r="2.5" fill="${INK}" stroke="none"/>`;
  }
  s += `<circle cx="200" cy="200" r="18" stroke-width="0.6"/>`;
  s += `<circle cx="200" cy="200" r="14" stroke-width="0.4"/>`;
  s += `</g>`;
  return s;
}

/** 七芒星（1つ飛ばし星形+内七角形、サブブックBタブ用） */
function geometricLayerHeptagram(): string {
  const outer = polyPoints(200, 200, 140, 7, 0);
  const starOrder = [0, 2, 4, 6, 1, 3, 5];
  const starPts = starOrder.map((i) => outer[i]);
  const inner = polyPoints(200, 200, 88, 7, 0);
  const innerRadial = outer.map(([x, y]) => {
    const dx = x - 200,
      dy = y - 200;
    const len = Math.sqrt(dx * dx + dy * dy);
    return [200 + (dx / len) * 88, 200 + (dy / len) * 88] as [number, number];
  });

  const f = (arr: [number, number][]) =>
    arr.map((p) => `${p[0].toFixed(1)},${p[1].toFixed(1)}`).join(" ");

  let s = `<g fill="none" stroke="${INK}">`;
  s += `<polygon points="${f(starPts)}" stroke-width="0.8"/>`;
  s += `<polygon points="${f(outer)}" stroke-width="0.4"/>`;
  s += `<polygon points="${f(inner)}" stroke-width="0.5"/>`;
  for (let i = 0; i < 7; i++) {
    const [x1, y1] = outer[i];
    const [x2, y2] = innerRadial[i];
    s += `<line x1="${x1.toFixed(1)}" y1="${y1.toFixed(1)}" x2="${x2.toFixed(1)}" y2="${y2.toFixed(1)}" stroke-width="0.3"/>`;
  }
  for (const [x, y] of outer) {
    s += `<circle cx="${x.toFixed(1)}" cy="${y.toFixed(1)}" r="4.5" stroke-width="0.5"/>`;
    s += `<circle cx="${x.toFixed(1)}" cy="${y.toFixed(1)}" r="2" fill="${INK}" stroke="none"/>`;
  }
  for (const [x, y] of inner) {
    s += `<circle cx="${x.toFixed(1)}" cy="${y.toFixed(1)}" r="2" fill="${INK}" stroke="none"/>`;
  }
  s += `<circle cx="200" cy="200" r="18" stroke-width="0.6"/>`;
  s += `<circle cx="200" cy="200" r="14" stroke-width="0.4"/>`;
  s += `</g>`;
  return s;
}

/** 八芒星（2正方形 45度ずらし、サブブックCタブ用） */
function geometricLayerOctagram(): string {
  const f = (arr: [number, number][]) =>
    arr.map((p) => `${p[0].toFixed(1)},${p[1].toFixed(1)}`).join(" ");

  const sq1 = polyPoints(200, 200, 140, 4, 0);
  const sq2 = polyPoints(200, 200, 140, 4, 45);
  const sqIn1 = polyPoints(200, 200, 90, 4, 0);
  const sqIn2 = polyPoints(200, 200, 90, 4, 45);
  const sqMini = polyPoints(200, 200, 55, 4, 22.5);
  const oct = polyPoints(200, 200, 140, 8, 22.5);
  const octIn = polyPoints(200, 200, 75, 8, 22.5);
  const star = polyPoints(200, 200, 140, 8, 0);
  const starIn = polyPoints(200, 200, 58, 8, 22.5);

  let s = `<g fill="none" stroke="${INK}">`;
  s += `<polygon points="${f(sq1)}" stroke-width="0.8"/>`;
  s += `<polygon points="${f(sq2)}" stroke-width="0.8"/>`;
  s += `<polygon points="${f(sqIn1)}" stroke-width="0.5"/>`;
  s += `<polygon points="${f(sqIn2)}" stroke-width="0.5"/>`;
  s += `<polygon points="${f(oct)}" stroke-width="0.4"/>`;
  s += `<polygon points="${f(octIn)}" stroke-width="0.4"/>`;
  s += `<polygon points="${f(sqMini)}" stroke-width="0.6"/>`;

  // 放射線（八方向）
  for (let i = 0; i < 8; i++) {
    s += `<line x1="${star[i][0].toFixed(1)}" y1="${star[i][1].toFixed(1)}" x2="${starIn[i][0].toFixed(1)}" y2="${starIn[i][1].toFixed(1)}" stroke-width="0.3"/>`;
  }

  // 頂点装飾
  for (const [x, y] of star) {
    s += `<circle cx="${x.toFixed(1)}" cy="${y.toFixed(1)}" r="4" stroke-width="0.5"/>`;
    s += `<circle cx="${x.toFixed(1)}" cy="${y.toFixed(1)}" r="2" fill="${INK}" stroke="none"/>`;
  }
  for (const [x, y] of octIn) {
    s += `<circle cx="${x.toFixed(1)}" cy="${y.toFixed(1)}" r="2" fill="${INK}" stroke="none"/>`;
  }

  // 中心
  s += `<circle cx="200" cy="200" r="5" fill="${INK}" stroke="none"/>`;
  s += `<circle cx="200" cy="200" r="18" stroke-width="0.6"/>`;
  s += `<circle cx="200" cy="200" r="14" stroke-width="0.4"/>`;

  s += `</g>`;
  return s;
}

// ── 公開API ──

/** 魔法陣パターンの種別 */
export type MagicCirclePattern =
  | "pentagram"
  | "hexagram"
  | "heptagram"
  | "octagram";

/**
 * 魔法陣SVGを生成し、grimoireコンテナに挿入する。
 * CSS `.grimoire__magic-circle` でサイズ・opacityを制御。
 * `--grimoire-ink` CSS変数でストローク色を制御。
 */
export function insertMagicCircle(
  container: HTMLElement,
  pattern: MagicCirclePattern = "pentagram"
): void {
  const geo =
    pattern === "octagram"
      ? geometricLayerOctagram()
      : pattern === "hexagram"
        ? geometricLayerHexagram()
        : pattern === "heptagram"
          ? geometricLayerHeptagram()
          : geometricLayerPentagram();
  const svg = `<svg viewBox="0 0 400 400" xmlns="http://www.w3.org/2000/svg">${circlesLayer()}${textLayer()}${geo}</svg>`;

  const wrapper = document.createElement("div");
  wrapper.className = "grimoire__magic-circle";
  wrapper.setAttribute("aria-hidden", "true");
  wrapper.innerHTML = svg;

  container.insertBefore(wrapper, container.firstChild);
}
